<script setup>
/**
 * The shell's identity anchor: the user avatar at the bottom of the product
 * rail. Opens sideways with availability, theme, profile, account switching
 * ("Switch account" only with more than one account, "New account" when the
 * install allows it) and logout. The old standalone account switcher is gone;
 * this menu carries both the user and their accounts.
 */
import { computed, ref } from 'vue';
import Auth from 'dashboard/api/auth';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import { useI18n } from 'vue-i18n';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';

const emit = defineEmits(['close', 'showCreateAccountModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const store = useStore();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');
const globalConfig = useMapGetter('globalConfig/get');
const { accountId } = useAccount();
const { isImpersonating } = useImpersonation();

// Account switching lives inside the profile menu now that the rail avatar is
// the user rather than the account.
const userAccounts = computed(() =>
  [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  )
);
const showAccountSwitcher = computed(() => userAccounts.value.length > 1);

const onChangeAccount = newId => {
  window.location.href = `/app/accounts/${newId}/dashboard`;
};

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ].map((label, index) => ({
    label,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}

const autoOfflineToggle = computed({
  get: () => currentUserAutoOffline.value,
  set: autoOffline => {
    store.dispatch('updateAutoOffline', {
      accountId: currentAccountId.value,
      autoOffline,
    });
  },
});

const isSuperAdmin = computed(() => currentUser.value.type === 'SuperAdmin');

const activeColorScheme = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto'
);

const themeOptions = computed(() => [
  { key: 'light', label: t('COMMAND_BAR.COMMANDS.LIGHT_MODE') },
  { key: 'dark', label: t('COMMAND_BAR.COMMANDS.DARK_MODE') },
  { key: 'auto', label: t('COMMAND_BAR.COMMANDS.SYSTEM_MODE') },
]);

function changeColorScheme(scheme) {
  activeColorScheme.value = scheme;
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, scheme);
  setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
}
</script>

<template>
  <!-- The menu is far wider than the rail's column, so the container's
       zero-size absolute wrapper is flattened and the menu anchors to the
       avatar itself, letting it sit beside the rail instead of under it. -->
  <DropdownContainer
    class="relative min-w-0 [&>div.absolute]:static"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        v-tooltip.right="currentUser.available_name"
        type="button"
        :aria-label="currentUser.available_name"
        class="grid size-10 flex-shrink-0 cursor-pointer place-items-center rounded-[10px] outline outline-1 outline-transparent hover:bg-n-rail-hover focus-visible:outline-2 focus-visible:outline-n-rail-indicator shell:size-[46px]"
        :class="{ 'bg-n-rail-hover': isOpen }"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
        />
      </button>
    </template>
    <DropdownBody
      class="z-50 w-72 bottom-0 ltr:left-full ltr:ml-2 rtl:right-full rtl:mr-2"
    >
      <DropdownItem :link="{ name: 'profile_settings_index' }" class="gap-3">
        <Avatar
          :size="40"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
        />
        <div class="min-w-0">
          <div class="text-sm font-medium leading-5 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </DropdownItem>
      <DropdownItem preserve-open>
        <div
          class="flex w-full gap-1 p-0.5 rounded-lg bg-n-alpha-1"
          role="radiogroup"
          :aria-label="$t('SIDEBAR.SET_YOUR_AVAILABILITY')"
        >
          <button
            v-for="status in availabilityStatuses"
            :key="status.value"
            role="radio"
            :aria-checked="status.active"
            class="flex flex-1 gap-1.5 justify-center items-center py-1 text-xs rounded-md cursor-pointer"
            :class="
              status.active
                ? 'bg-n-background text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="changeAvailabilityStatus(status.value)"
          >
            <span class="size-2 rounded-full" :class="status.color" />
            {{ status.label }}
          </button>
        </div>
      </DropdownItem>
      <DropdownItem>
        <div class="flex-grow min-w-0 text-xs text-n-slate-11">
          {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
          <Icon
            v-tooltip.top="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_SHORT')"
            icon="i-lucide-info"
            class="inline-block align-middle ms-1 size-3.5 text-n-slate-10"
          />
        </div>
        <ToggleSwitch v-model="autoOfflineToggle" />
      </DropdownItem>
      <DropdownSeparator />
      <DropdownItem
        :label="t('SIDEBAR_ITEMS.MY_PROFILE')"
        icon="i-lucide-user-round"
        :link="{ name: 'profile_settings_index' }"
      />
      <DropdownItem preserve-open class="gap-3 w-full rounded-lg">
        <Icon icon="i-lucide-sun-moon" class="size-4 text-n-slate-11" />
        <span class="flex-grow min-w-0 text-sm text-n-slate-12">
          {{ t('SIDEBAR_ITEMS.THEME') }}
        </span>
        <div
          class="flex flex-shrink-0 gap-1 p-0.5 rounded-lg bg-n-alpha-1"
          role="radiogroup"
          :aria-label="t('SIDEBAR_ITEMS.THEME')"
        >
          <button
            v-for="option in themeOptions"
            :key="option.key"
            role="radio"
            :aria-checked="activeColorScheme === option.key"
            class="px-2 py-1 text-xs rounded-md cursor-pointer"
            :class="
              activeColorScheme === option.key
                ? 'bg-n-background text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="changeColorScheme(option.key)"
          >
            {{ option.label }}
          </button>
        </div>
      </DropdownItem>
      <DropdownItem
        v-if="isSuperAdmin"
        :label="t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE')"
        icon="i-lucide-castle"
        link="/super_admin"
        native-link
        target="_blank"
      />
      <template v-if="showAccountSwitcher">
        <DropdownSeparator />
        <DropdownSection :title="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')">
          <DropdownItem
            v-for="account in userAccounts"
            :id="`account-${account.id}`"
            :key="account.id"
            class="cursor-pointer"
            @click="onChangeAccount(account.id)"
          >
            <template #label>
              <div class="text-left rtl:text-right flex gap-2 items-center">
                <span
                  class="text-n-slate-12 max-w-36 truncate min-w-0"
                  :title="account.name"
                >
                  {{ account.name }}
                </span>
                <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
                <span class="text-n-slate-11 max-w-24 truncate capitalize">
                  {{
                    account.custom_role_id
                      ? account.custom_role.name
                      : account.role
                  }}
                </span>
              </div>
              <Icon
                v-show="account.id === accountId"
                icon="i-lucide-check"
                class="text-n-teal-11 size-5"
              />
            </template>
          </DropdownItem>
        </DropdownSection>
      </template>
      <DropdownItem
        v-if="globalConfig.createNewAccountFromDashboard"
        :label="t('CREATE_ACCOUNT.NEW_ACCOUNT')"
        icon="i-lucide-plus"
        :click="() => emit('showCreateAccountModal')"
      />
      <DropdownSeparator />
      <DropdownItem
        :click="Auth.logout"
        class="gap-3 w-full rounded-lg hover:bg-n-ruby-3"
      >
        <Icon icon="i-lucide-power" class="size-4 text-n-ruby-11" />
        <span class="text-sm text-n-ruby-11">
          {{ t('SIDEBAR_ITEMS.LOGOUT') }}
        </span>
      </DropdownItem>
    </DropdownBody>
  </DropdownContainer>
</template>
